import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type CancelMode = "period_end" | "immediate";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string | null;
  customerName?: string | null;
  onCancelled?: (result: {
    mode: CancelMode;
    cancel_at_period_end: boolean;
    status: string;
  }) => void;
};

const CancelSubscriptionDialog = ({
  open,
  onOpenChange,
  subscriptionId,
  customerName,
  onCancelled,
}: Props) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<CancelMode>("period_end");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!subscriptionId || busy) return;
    setBusy(true);
    try {
      const { data, error } = await (supabase as any).functions.invoke(
        "admin-cancel-subscription",
        { body: { subscription_id: subscriptionId, mode } },
      );
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Kunde inte avsluta prenumerationen");
      }
      toast({
        title: "Prenumerationen är uppsagd",
        description:
          mode === "period_end"
            ? "Den avslutas när den betalda perioden löper ut."
            : "Den avslutades direkt och kommande obetald leverans togs bort.",
      });
      onCancelled?.({
        mode,
        cancel_at_period_end: !!data.cancel_at_period_end,
        status: data.status ?? "cancelled",
      });
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Något gick fel",
        description: e?.message || "Försök igen.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Avsluta prenumeration</AlertDialogTitle>
          <AlertDialogDescription>
            {customerName ? `${customerName} — v` : "V"}älj när uppsägningen ska gälla. Åtgärden
            görs hos betalleverantören och kan inte ångras här.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <RadioGroup
          value={mode}
          onValueChange={(v) => setMode(v as CancelMode)}
          className="space-y-3 py-2"
        >
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="period_end" id="cancel-period-end" className="mt-1" />
            <Label htmlFor="cancel-period-end" className="font-normal cursor-pointer">
              <span className="font-medium block">Vid periodens slut</span>
              <span className="text-xs text-muted-foreground">
                Kunden behåller den leverans som redan är betald.
              </span>
            </Label>
          </div>
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="immediate" id="cancel-immediate" className="mt-1" />
            <Label htmlFor="cancel-immediate" className="font-normal cursor-pointer">
              <span className="font-medium block">Direkt</span>
              <span className="text-xs text-muted-foreground">
                Avslutas omedelbart. Kommande obetald leverans tas bort och öppen faktura
                makuleras.
              </span>
            </Label>
          </div>
        </RadioGroup>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Avbryt</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={busy}
          >
            {busy ? "Avslutar…" : "Avsluta prenumeration"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelSubscriptionDialog;
